import {
  Box,
  createListCollection,
  Input,
  Portal,
  Select,
  Text,
} from '@chakra-ui/react'
import { CheckIcon, ChevronDown } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import { useMemo, useState } from 'react'

import { Checkbox, Field } from '@mangovoice/mango-ui'

import type { PbxSettingsTranslateFn } from '../types'
import { text } from '../types'
import type { SelectOption } from '../types/pbxSettings.types'

const controlFocusStyles = {
  bg: 'bg.subtle',
  borderColor: 'border.default',
  borderRadius: 'lg',
  _focusVisible: {
    borderColor: 'blue.500',
    boxShadow: '0 0 0 2px var(--chakra-colors-blue-500)',
  },
}

const NUMBER_INPUT_BLOCKED_KEYS = new Set(['-', '+', 'e', 'E', '.'])

export const SelectField = ({
  t,
  label,
  value,
  onChange,
  options,
  placeholder,
  helperText,
  required = false,
  disabled = false,
  loading = false,
  disabledValues,
}: {
  t?: PbxSettingsTranslateFn
  label: string
  value: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  helperText?: string
  required?: boolean
  disabled?: boolean
  loading?: boolean
  disabledValues?: Set<string>
}) => {
  const hasMatchingOption = !value || options.some((o) => o.value === value)
  const mergedOptions = useMemo(() => {
    if (hasMatchingOption || !value) return options
    return [{ value, label: value }, ...options]
  }, [hasMatchingOption, options, value])

  const collection = useMemo(
    () =>
      createListCollection({
        items: mergedOptions.map((option) => ({
          label: option.label,
          value: option.value,
          disabled:
            option.disabled || (disabledValues?.has(option.value) ?? false),
        })),
      }),
    [disabledValues, mergedOptions],
  )

  return (
    <Field.Root
      required={required}
      display="grid"
      gridTemplateColumns="1fr"
      gap={2}
    >
      <Field.Label>{label}</Field.Label>
      <Select.Root
        collection={collection}
        value={value ? [value] : []}
        onValueChange={(details) => onChange(details.value[0] ?? '')}
        disabled={disabled || loading}
      >
        <Select.HiddenSelect />
        <Select.Control>
          <Select.Trigger {...controlFocusStyles}>
            <Select.ValueText
              placeholder={
                loading
                  ? text(t, 'pbxSettings.common.loading', 'Loading...')
                  : (placeholder ??
                    text(t, 'pbxSettings.common.select', 'Select'))
              }
            />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator>
              <ChevronDown size={16} />
            </Select.Indicator>
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content>
              {collection.items.map((item) => (
                <Select.Item item={item} key={`${label}-${item.value}`}>
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator>
                    <CheckIcon size={14} />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
      {helperText && (
        <Text color="fg.muted" fontSize="sm">
          {helperText}
        </Text>
      )}
    </Field.Root>
  )
}

export const InputField = ({
  t,
  label,
  value,
  onChange,
  required = false,
  type = 'text',
  min,
  max,
}: {
  t?: PbxSettingsTranslateFn
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  type?: 'text' | 'number'
  min?: number
  max?: number
}) => {
  const isNumberInput = type === 'number'
  const [hasBlurred, setHasBlurred] = useState(false)
  const numericValue = Number.parseInt(value, 10)
  const showMinError =
    isNumberInput &&
    hasBlurred &&
    min !== undefined &&
    value.trim() !== '' &&
    Number.isFinite(numericValue) &&
    numericValue < min
  const showMaxError =
    isNumberInput &&
    hasBlurred &&
    max !== undefined &&
    value.trim() !== '' &&
    Number.isFinite(numericValue) &&
    numericValue > max
  const rangeErrorMessage = (() => {
    if (showMinError && min !== undefined) {
      return text(
        t,
        'pbxSettings.validation.numericFieldMin',
        `Minimum is ${min}.`,
        { min },
      )
    }
    if (showMaxError && max !== undefined) {
      return text(
        t,
        'pbxSettings.validation.numericFieldMax',
        `Maximum is ${max}.`,
        { max },
      )
    }
    return null
  })()

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isNumberInput && NUMBER_INPUT_BLOCKED_KEYS.has(event.key)) {
      event.preventDefault()
    }
  }

  const handleChange = (nextValue: string) => {
    onChange(isNumberInput ? nextValue.replace(/\D/g, '') : nextValue)
  }

  return (
    <Field.Root
      invalid={Boolean(rangeErrorMessage)}
      required={required}
      display="grid"
      gridTemplateColumns="1fr"
      gap={2}
    >
      <Field.Label>{label}</Field.Label>
      <Input
        {...controlFocusStyles}
        type={type}
        min={min}
        max={max}
        step={isNumberInput ? 1 : undefined}
        inputMode={isNumberInput ? 'numeric' : undefined}
        pattern={isNumberInput ? '[0-9]*' : undefined}
        value={value}
        onKeyDown={handleKeyDown}
        onChange={(event) => handleChange(event.target.value)}
        onFocus={() => setHasBlurred(false)}
        onBlur={() => setHasBlurred(true)}
      />
      {rangeErrorMessage ? (
        <Field.ErrorText>{rangeErrorMessage}</Field.ErrorText>
      ) : null}
    </Field.Root>
  )
}

export const BooleanSelectField = ({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
}: {
  label: string
  value: boolean
  onChange: (value: boolean) => void
  required?: boolean
  disabled?: boolean
}) => (
  <Box minH="40px" display="flex" alignItems="center">
    <Checkbox
      gap={0}
      checked={value}
      disabled={disabled}
      onCheckedChange={(details) => onChange(Boolean(details.checked))}
    >
      {label}
      {required ? (
        <Text as="span" color="fg.error" ml={1}>
          *
        </Text>
      ) : null}
    </Checkbox>
  </Box>
)

export const HeadingText = ({ children }: { children: string }) => (
  <Text fontSize="xl" fontWeight="semibold" mb={1}>
    {children}
  </Text>
)
